import React, { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { CONTACT, waLink, mailLink } from "@/data/site";
import { api, formatApiErrorDetail } from "@/lib/api";
import GoogleReviews from "@/components/site/GoogleReviews";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MapPin, Phone, Clock, MessageCircle, Loader2, Mail, Instagram, Globe } from "lucide-react";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-3 focus-visible:ring-2 focus-visible:ring-turq outline-none text-navy h-auto";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapsQuery)}&output=embed`;
  const directionsUrl = CONTACT.mapsLink;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) { toast.error("Please fill all required fields."); return; }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <section id="contact" className="py-20 md:py-28" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-turq-dark">{t("contact_label")}</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mt-3 tracking-tight">{t("contact_title")}</h2>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-5">
            <div className="rounded-[2rem] overflow-hidden shadow-[0_20px_60px_-25px_rgba(10,37,64,0.4)] border border-slate-100">
              <iframe title="DOABA Diagnostics Location" src={mapSrc} className="w-full h-[280px] border-0" loading="lazy" data-testid="contact-map" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-5 border border-slate-100 flex gap-3" data-testid="contact-address">
                <span className="w-10 h-10 rounded-full bg-turq/10 flex items-center justify-center text-turq-dark shrink-0"><MapPin className="w-5 h-5" /></span>
                <div><p className="font-semibold text-navy text-sm">Address</p><p className="text-secondary text-sm">{CONTACT.addressLines.join(", ")}</p></div>
              </div>
              <a href={`tel:${CONTACT.phoneRaw}`} className="bg-white rounded-2xl p-5 border border-slate-100 flex gap-3" data-testid="contact-phone">
                <span className="w-10 h-10 rounded-full bg-turq/10 flex items-center justify-center text-turq-dark shrink-0"><Phone className="w-5 h-5" /></span>
                <div><p className="font-semibold text-navy text-sm">Phone</p><p className="text-secondary text-sm">{CONTACT.phoneDisplay}</p></div>
              </a>
              <a href={mailLink} className="bg-white rounded-2xl p-5 border border-slate-100 flex gap-3" data-testid="contact-email">
                <span className="w-10 h-10 rounded-full bg-turq/10 flex items-center justify-center text-turq-dark shrink-0"><Mail className="w-5 h-5" /></span>
                <div className="min-w-0"><p className="font-semibold text-navy text-sm">Email</p><p className="text-secondary text-sm truncate">{CONTACT.email}</p></div>
              </a>
              <div className="bg-white rounded-2xl p-5 border border-slate-100 flex gap-3" data-testid="contact-hours">
                <span className="w-10 h-10 rounded-full bg-turq/10 flex items-center justify-center text-turq-dark shrink-0"><Clock className="w-5 h-5" /></span>
                <div><p className="font-semibold text-navy text-sm">Hours</p>{CONTACT.hours.map((h) => <p key={h.day} className="text-secondary text-xs">{h.day}: {h.time}</p>)}</div>
              </div>
              <a href={CONTACT.websiteUrl} target="_blank" rel="noreferrer" className="bg-white rounded-2xl p-5 border border-slate-100 flex gap-3" data-testid="contact-website">
                <span className="w-10 h-10 rounded-full bg-turq/10 flex items-center justify-center text-turq-dark shrink-0"><Globe className="w-5 h-5" /></span>
                <div className="min-w-0"><p className="font-semibold text-navy text-sm">Website</p><p className="text-secondary text-sm truncate">{CONTACT.website}</p></div>
              </a>
              <a href={CONTACT.instagram} target="_blank" rel="noreferrer" className="bg-white rounded-2xl p-5 border border-slate-100 flex gap-3" data-testid="contact-instagram">
                <span className="w-10 h-10 rounded-full bg-turq/10 flex items-center justify-center text-turq-dark shrink-0"><Instagram className="w-5 h-5" /></span>
                <div className="min-w-0"><p className="font-semibold text-navy text-sm">Instagram</p><p className="text-secondary text-sm truncate">{CONTACT.instagramHandle}</p></div>
              </a>
              <a href={waLink()} target="_blank" rel="noreferrer" className="bg-emerald-500 rounded-2xl p-5 flex gap-3 text-white hover:bg-emerald-600 transition-colors sm:col-span-2" data-testid="contact-whatsapp">
                <span className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0"><MessageCircle className="w-5 h-5" /></span>
                <div><p className="font-semibold text-sm">WhatsApp</p><p className="text-white/80 text-sm">Chat with us now</p></div>
              </a>
            </div>
          </motion.div>

          <motion.form initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} onSubmit={submit}
            className="bg-white rounded-[2rem] p-7 md:p-9 border border-slate-100 shadow-[0_20px_60px_-25px_rgba(10,37,64,0.2)]" data-testid="contact-form">
            <h3 className="font-heading text-xl font-semibold text-navy">Send us a message</h3>
            <div className="space-y-4 mt-5">
              <div><Label className="text-navy text-sm mb-1.5 block">Name *</Label><Input className={inputCls} value={form.name} onChange={set("name")} data-testid="contact-name-input" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-navy text-sm mb-1.5 block">Phone *</Label><Input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" /></div>
                <div><Label className="text-navy text-sm mb-1.5 block">Email</Label><Input className={inputCls} value={form.email} onChange={set("email")} data-testid="contact-email-input" /></div>
              </div>
              <div><Label className="text-navy text-sm mb-1.5 block">Message *</Label><Textarea className={inputCls} rows={4} value={form.message} onChange={set("message")} data-testid="contact-message-input" /></div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-turq hover:bg-turq-dark text-white py-6 text-base" data-testid="contact-submit-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("sending")}</> : <><Mail className="w-4 h-4 mr-2" /> {t("submit")}</>}
              </Button>
            </div>
          </motion.form>
        </div>
        <div className="mt-8">
          <GoogleReviews compact />
        </div>
      </div>
    </section>
  );
}
